import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, handleAxiosError, type ApiResponse } from "@/helpers/axios";

export type AdminUpdateAccountBody = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  username: string;
  role: string;
  isActive: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  country: string;
  accountType: "default" | "google";
  onboardingCompleted: boolean;
  trialUsed: boolean;
};

export const useUpdateAccountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      body,
    }: {
      accountId: string;
      body: AdminUpdateAccountBody;
    }) => {
      const response = await axiosInstance.put<ApiResponse<unknown>>(
        `/v2/accounts/${accountId}`,
        body
      );
      return response.data;
    },
    onSuccess: (_, { accountId }) => {
      void queryClient.invalidateQueries({ queryKey: ["account-admin-details", accountId] });
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
};

export type AdminPatchAccountBody = Partial<AdminUpdateAccountBody>;

export const usePatchAccountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      body,
    }: {
      accountId: string;
      body: AdminPatchAccountBody;
    }) => {
      const response = await axiosInstance.patch<ApiResponse<unknown>>(
        `/v2/accounts/${accountId}`,
        body
      );
      return response.data;
    },
    onSuccess: (_, { accountId }) => {
      void queryClient.invalidateQueries({ queryKey: ["account-admin-details", accountId] });
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
};

export const getUpdateAccountErrorMessage = (error: unknown): string =>
  handleAxiosError(error)?.message ?? "Could not save changes.";

export const getPatchAccountErrorMessage = getUpdateAccountErrorMessage;
